// PRC-048 (diseño 33, apartado 6.4): guardado diferido con «Deshacer».
// Al confirmar, la fila sale de la lista y aparece el aviso con «Deshacer»
// unos 6 segundos. La escritura real se hace al terminar ese tiempo o al salir
// de la pantalla. Con «Deshacer» no se escribe nada y la fila vuelve. Si la
// escritura falla, la fila vuelve y se muestra el error.
import { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '@mui/material';
import { useSnackbar, type SnackbarKey } from 'notistack';
import { useQueryClient } from '@tanstack/react-query';

export const DEFERRED_SAVE_MS = 6000;

export type CadenceListTab = 'pending' | 'in_follow_up' | 'resolved' | 'historical';

export interface DeferredCadenceAction {
  /** Clave de la fila (episodio o relación). */
  key: string;
  /** Mensaje del aviso («Aviso archivado», «Seguimiento abierto»…). */
  message: string;
  /** Pestaña de la que sale y a la que va (para ajustar los recuentos). */
  from: CadenceListTab;
  to: CadenceListTab;
  run: () => Promise<unknown>;
}

interface PendingEntry {
  action: DeferredCadenceAction;
  timer: number;
  snackbarKey: SnackbarKey;
  /** La escritura ya ha empezado: sigue oculta y contada, pero no se repite ni se deshace. */
  running: boolean;
}

const errorText = (error: unknown): string => {
  const detail = (error as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  return typeof detail === 'string' && detail.trim()
    ? detail
    : 'No se pudo guardar. La fila vuelve a la lista; inténtalo de nuevo.';
};

export function useDeferredCadenceActions() {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const pendingRef = useRef(new Map<string, PendingEntry>());
  const [pending, setPending] = useState<DeferredCadenceAction[]>([]);

  const sync = useCallback(() => {
    setPending(Array.from(pendingRef.current.values()).map((entry) => entry.action));
  }, []);

  const refresh = useCallback(() => Promise.all([
    queryClient.invalidateQueries({ queryKey: ['margin-center', 'cadence-attention'] }),
    queryClient.invalidateQueries({ queryKey: ['margin-center', 'cadence-management'] }),
    queryClient.invalidateQueries({ queryKey: ['margin-center', 'follow-ups'] }),
  ]).catch(() => undefined), [queryClient]);

  const execute = useCallback((key: string) => {
    const entry = pendingRef.current.get(key);
    // Una escritura ya en marcha nunca se repite (salir de la pantalla mientras corre).
    if (!entry || entry.running) return;
    entry.running = true;
    window.clearTimeout(entry.timer);
    closeSnackbar(entry.snackbarKey);
    entry.action.run()
      // La fila sigue oculta hasta que la lista recargada ya no la trae.
      .then(() => refresh())
      .then(() => {
        pendingRef.current.delete(key);
        sync();
      })
      .catch((error: unknown) => {
        pendingRef.current.delete(key);
        sync();
        void refresh();
        enqueueSnackbar(errorText(error), { variant: 'error' });
      });
  }, [closeSnackbar, enqueueSnackbar, refresh, sync]);

  const undo = useCallback((key: string) => {
    const entry = pendingRef.current.get(key);
    // Lo que ya se está escribiendo no se puede deshacer.
    if (!entry || entry.running) return;
    window.clearTimeout(entry.timer);
    closeSnackbar(entry.snackbarKey);
    pendingRef.current.delete(key);
    sync();
  }, [closeSnackbar, sync]);

  const schedule = useCallback((action: DeferredCadenceAction) => {
    if (pendingRef.current.has(action.key)) return;
    const snackbarKey = enqueueSnackbar(action.message, {
      autoHideDuration: DEFERRED_SAVE_MS,
      persist: false,
      action: () => (
        <Button size="small" onClick={() => undo(action.key)} sx={{ color: '#7dd3fc', fontWeight: 800, textTransform: 'none' }}>
          Deshacer
        </Button>
      ),
    });
    const timer = window.setTimeout(() => execute(action.key), DEFERRED_SAVE_MS);
    pendingRef.current.set(action.key, { action, timer, snackbarKey, running: false });
    sync();
  }, [enqueueSnackbar, execute, sync, undo]);

  // Al salir de la pantalla (o cerrar la pestaña), se escribe lo pendiente.
  useEffect(() => {
    const flushAll = () => {
      Array.from(pendingRef.current.keys()).forEach((key) => execute(key));
    };
    window.addEventListener('beforeunload', flushAll);
    return () => {
      window.removeEventListener('beforeunload', flushAll);
      flushAll();
    };
  }, [execute]);

  const pendingKeys = new Set(pending.map((action) => action.key));

  /** Recuento visible de una pestaña con lo pendiente aplicado. */
  const adjustCount = (tab: CadenceListTab, serverCount: number | null | undefined): number | null => {
    if (serverCount == null) return null;
    const out = pending.filter((action) => action.from === tab).length;
    const incoming = pending.filter((action) => action.to === tab).length;
    return Math.max(0, serverCount - out + incoming);
  };

  return { schedule, undo, pending, pendingKeys, adjustCount };
}

export type DeferredCadenceActions = ReturnType<typeof useDeferredCadenceActions>;

export const cadenceRowKey = (customerId: string, productId: string, firstOutside?: string | null) =>
  `${customerId}|${productId}|${firstOutside ?? 'manual'}`;

export default useDeferredCadenceActions;
